/**
 * 设备报警配置
 */
import React, { useState } from 'react';
import { Card, message } from 'antd';
import SearchForm from './components/SearchForm';
import AlarmConfigTable from './components/AlarmConfigTable';
import DetailModal from './components/DetailModal';
import styles from '@/styles/pageContent.module.scss';
import { getAlarmListByPlate, getAlarmListByAlarmType, deleteAlarmPush } from '@/service/jtAlarmConfig';
import { getAlarmTypeCodeFromValue } from '@/utils/jtDeviceAlarm';

const JtAlarmConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(40);
  const [totalCount, setTotalCount] = useState(0);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [modalMode, setModalMode] = useState<'create' | 'view' | 'edit'>('view');
  const [aggregationType, setAggregationType] = useState<string>('');
  const [lastSearchParams, setLastSearchParams] = useState<any>(null); // 保存最后一次查询参数

  // 打开新建弹窗
  const handleCreate = () => {
    setModalMode('create');
    setCurrentRecord(null);
    setDetailModalVisible(true);
  };

  // 打开详情弹窗（查看模式）
  const handleOpenDetail = (record: any) => {
    setModalMode('view');
    setCurrentRecord(record);
    setDetailModalVisible(true);
  };

  // 打开编辑弹窗
  const handleEdit = (record: any) => {
    setModalMode('edit');
    setCurrentRecord(record);
    setDetailModalVisible(true);
  };

  // 关闭详情弹窗
  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setCurrentRecord(null);
  };

  // 获取数据的核心函数
  const fetchData = async (searchParams: any, currentPage: number, currentLimit: number) => {
    if (!searchParams) return;
    
    setLoading(true);
    try {
      let res: any;

      const params = { ...searchParams, page: currentPage, limit: currentLimit };

      // 处理报警类型参数
      if (params.alarmTypes && Array.isArray(params.alarmTypes)) {
        params.alarmTypeList = params.alarmTypes.map((item: string[]) => {
          const [alarmType, warningType] = getAlarmTypeCodeFromValue(item);
          return { alarmType, warningType };
        });
        delete params.alarmTypes;
      }

      if (searchParams.aggregationType === 'plate') {
        res = await getAlarmListByPlate(params);
      } else if (searchParams.aggregationType === 'alarmType') {
        res = await getAlarmListByAlarmType(params);
      }

      if (res && res.data?.data) {
        const data = res.data.data;
        
        console.log('查询结果:', data);
        // if (Array.isArray(data)) {
          setData(data);
          setTotalCount(res.data.count);
        // } else {
        //   // 尝试适配列表结构
        //   const list = data.list || data.records;
        //   if (Array.isArray(list)) {
        //     setData(list);
        //     // setTotalCount(data.count || data.total || list.length);
        //   } else {
        //     // 兜底：作为单条数据处理
        //     setData([data]);
        //     setTotalCount(1);
        //   }
        // }
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('查询失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 创建成功回调
  const handleCreateSuccess = () => {
    // 如果有上次的查询参数，重新查询刷新列表
    if (lastSearchParams) {
      fetchData(lastSearchParams, page, limit);
    }
  };

  // 查询回调
  const handleSearch = async (values: any) => {
    console.log('搜索参数:', values);
    setAggregationType(values.aggregationType);
    setLastSearchParams(values); // 保存查询参数
    setPage(1); // 重置页码
    await fetchData(values, 1, limit); // 使用页码1进行查询
  };

  // 分页变化
  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setLimit(newPageSize);
    // 重新请求数据
    if (lastSearchParams) {
      fetchData(lastSearchParams, newPage, newPageSize);
    }
  };

  // 删除配置
  const handleDelete = async (record: any) => {
    try {
      // 根据聚合类型获取要删除的id列表
      let ids: number[] = [];
      if (aggregationType === 'plate') {
        // 按车牌聚合：删除该车辆的所有报警配置
        ids = record.alarmTypeList?.map((item: any) => item.id).filter((id: number) => id !== undefined) || [];
      } else if (aggregationType === 'alarmType') {
        // 按报警类型聚合：删除该报警类型下的所有车辆配置
        ids = record.plateNumberList?.map((item: any) => item.id).filter((id: number) => id !== undefined) || [];
      }

      if (ids.length === 0) {
        message.warning('未找到可删除的配置');
        return;
      }

      const res = await deleteAlarmPush(ids);
      if (res?.data?.success) {
        message.success('删除成功');
        // 删除成功后刷新列表
        if (lastSearchParams) {
          fetchData(lastSearchParams, page, limit);
        }
      } else {
        message.error(res?.data?.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败，请稍后重试');
    }
  };

  return (
    <Card bordered={false} className={styles.fixPageContentBox}>
      <SearchForm onSearch={handleSearch} onOpenDetail={handleOpenDetail} onCreate={handleCreate} />
      <AlarmConfigTable
        data={data}
        loading={loading}
        page={page}
        limit={limit}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onOpenDetail={handleOpenDetail}
        onEdit={handleEdit}
        onDelete={handleDelete}
        aggregationType={aggregationType}
      />
      <DetailModal
        visible={detailModalVisible}
        onClose={handleCloseDetail}
        onSuccess={handleCreateSuccess}
        record={currentRecord}
        mode={modalMode}
        aggregationType={aggregationType}
      />
    </Card>
  );
};

export default JtAlarmConfig;
